/**
 * The equipment item catalog — the single source of truth for what gear
 * exists is `equipment.store` (repo root); this module imports its generated
 * projection `items.data.ts` (do not edit — run `node tools/equipment-store.js`
 * or save in the knobs app's Gear tab). Everything downstream reads this surface:
 * the sim's equip validation, the arena gear panel, the hero paper-doll
 * (outfitForEquipment), play.js.
 *
 * Kinds, not entities: items never leave the party (no drop/trade/floor-loot
 * yet), so a player's gear is fully described by kind indices: `equipment`
 * (slot → kind) + `items` (unequipped kinds, grid order). Kind indices are
 * the stable wire/state ids — PERMANENT store ids (never renumbered, never
 * reused; the catalog may have gaps after deletions). A player's STARTING
 * loadout is not item data: the game spawns bare (profiles are a later
 * system) and the sandboxes (arena, scenarios, tests) pass an explicit
 * loadout at state creation.
 *
 * Equipment SLOTS are game categories (weapon/shield/helmet/chest/legs/feet),
 * not the hero pipeline's render slots (weapon/shield/head/torso/leg/...) —
 * leg armour and footwear have independent render slots and layers, so
 * greaves and boots remain visible together. The mapping is
 * HERO_SLOT below; the client's render mirror (client/src/hero.ts) reads it.
 */
import { ITEMS_DATA } from "./items.data.js";

export const EQUIP_SLOTS = ["weapon", "shield", "helmet", "chest", "legs", "feet"] as const;
export type EquipSlot = (typeof EQUIP_SLOTS)[number];

/** Game slot → hero part-pipeline render slot (atlas hero.outfit). Exported
 *  so fixtures (scenarios, tests) can build ItemDefs without a heroVariant
 *  lookup; the client's render mirror (client/src/hero.ts) reads the same map. */
/**
 * A weapon's DELIVERY class (weapons only — armor carries none). "melee" is
 * a swing at reach (the `range` stat is the reach; a long melee weapon is
 * still a swing, no missile); "ranged"/"magic" fire a projectile at the
 * weapon's `range` stat. "magic" additionally carries a SPELL (spellType /
 * spellColor / spellRadius on ItemDef) — the client draws the weapon's
 * attack as that spell (the sim resolves delivery identically; the spell
 * fields are presentation data the sim never reads).
 */
export type WeaponClass = "melee" | "ranged" | "magic";
export const WEAPON_CLASSES = ["melee", "ranged", "magic"] as const;

/**
 * The spell effect families the client can draw (packages/client
 * effects.ts). "basic-bolt" is the bolt the shade has always cast — now a
 * named, reshadeable, resizable effect any magic weapon can wield. A new
 * family = a client effect + a line here + a store spellType value.
 */
export type SpellType = "basic-bolt";
export const SPELL_TYPES = ["basic-bolt"] as const;

export const HERO_SLOT: Readonly<Record<EquipSlot, string>> = {
  weapon: "weapon",
  shield: "shield",
  helmet: "head",
  chest: "torso",
  legs: "leg",
  feet: "feet",
};

/**
 * Combat stats, data-driven so a new piece of gear carries its own numbers and
 * the arena / game can test it without code. The combat model is TWO ROLLS —
 * THE ACCURACY CONTEST (shared/constants.ts): attack POINTS vs defense POINTS
 * decide how OFTEN an attack lands (hitChanceBps); a hit lands the weapon's
 * FULL damage — defense never soaks damage. All values are integers
 * (AGENTS §3); speeds are authored in ticks (the sim consumes them directly;
 * clients display the × TICK_MS ms equivalent, display-only).
 *
 * The stat map is open-ended: keys the sim does not know yet (future crit /
 * dodge / requirements) are carried from the store and ignored — no migration
 * when a new stat lands. Legacy `attack` migrates to both accuracy buckets;
 * obsolete `accuracy`/`defense` keys are rejected by the store parser.
 */
export const SIGNED_EQUIPMENT_STATS = ["meleeAttack", "magicAttack", "meleeDamageBonusPct", "magicDamageBonusPct", "meleeDef", "magicDef"] as const;
export function isSignedEquipmentStat(key: string): boolean {
  return (SIGNED_EQUIPMENT_STATS as readonly string[]).includes(key);
}

export interface ItemStats {
  /** Weapon: damage range, inclusive (integers). FULL damage lands on a hit —
   *  never mitigated by defense. */
  atkMin?: number;
  atkMax?: number;
  /** Weapon: swing cadence in TICKS (one full swing; × TICK_MS = the ms a
   *  client displays). The sim consumes ticks directly. */
  speedTicks?: number;
  /** Legacy single accuracy stat, accepted only to preserve older fixtures. */
  attack?: number;
  /** Signed accuracy points; weapon base + armour bonuses + level bonus. */
  meleeAttack?: number;
  magicAttack?: number;
  /** Signed damage percentages, summed across all worn gear. */
  meleeDamageBonusPct?: number;
  magicDamageBonusPct?: number;
  /** Armor: signed defense POINTS vs melee+ranged attacks. Stacks additively across
   *  armor slots; contests the attacker's attack on the curve
   *  (hitChanceBps) — never reduces damage. */
  meleeDef?: number;
  /** Armor: signed defense POINTS vs magic attacks (same contest, other bucket). */
  magicDef?: number;
  /** Armor: max HP added to the wearer. */
  maxHp?: number;
  /** Open-ended: future stats parse from the store and ride along here. */
  [key: string]: number | undefined;
}

export interface ItemDef {
  /** Catalog kind index — the deterministic item id on the wire and in state
   *  (a permanent equipment.store id; the catalog may have gaps). */
  kind: number;
  /** Display name (HUD, chat). */
  name: string;
  /** The equipment slot this kind fills. */
  slot: EquipSlot;
  /** Hero part pipeline slot the part composites onto (atlas hero.outfit). */
  heroSlot: string;
  /** Hero part pipeline variant — variant + shade suffix for shaded items
   *  (e.g. "sword-ce8946" for a #CE8946 Bronze Sword; the pipeline's tree
   *  key for its baked frames). */
  heroVariant: string;
  /** Atlas items-section id for the 48×48 icon (icon shape + shade suffix). */
  icon: string;
  /** The item's shade hex (#rrggbb) or null (pipeline canon colours). */
  shade: string | null;
  /** Combat stats (data-driven — see ItemStats). Absent for non-combat items. */
  stats?: ItemStats;
  /** The weapon's delivery class (weapons only; absent for armor). Absent
   *  = "melee" — the pre-class catalog is unchanged. */
  weaponClass?: WeaponClass;
  /** The spell the weapon casts (required exactly for weaponClass "magic"). */
  spellType?: SpellType;
  /** The spell's reshade hex (#rrggbb) — the basic-bolt's hue. */
  spellColor?: string;
  /** The spell's size scale in px at 1x (integer; the canon bolt is 11). */
  spellRadius?: number;
}

/**
 * One row of the generated `items.data.ts` projection — typed (the data
 * module is annotated with it) so a hand-edited or stale projection that
 * drops a required field is a TYPECHECK error, not just the catalog's
 * load-time throw. The runtime validation below still runs: it catches
 * VALUE errors (duplicate kinds, illegal stats) that types can't see.
 */
export interface ItemDataRow {
  kind: number;
  name: string;
  slot: EquipSlot;
  heroSlot: string;
  heroVariant: string;
  icon: string;
  shade: string | null;
  stats: ItemStats;
  /** Weapons only; absent = "melee" (the pre-class default). */
  weaponClass?: WeaponClass;
  /** Magic weapons only (the three spell fields are all-or-nothing). */
  spellType?: SpellType;
  spellColor?: string;
  spellRadius?: number;
}

/**
 * All equippable item kinds. Built from the generated data with the same
 * validation discipline the hand-written catalog had: unique kinds, legal
 * slots, integer stats. A bad store is a build error, never a runtime
 * surprise.
 */
function buildCatalog(): readonly ItemDef[] {
  const kinds = new Set<number>();
  const out: ItemDef[] = [];
  // Widen the `as const` literal types: the generated data is validated here
  // at runtime (a hand-edited items.data.ts must fail loudly, not type-trick
  // its way past the checks).
  const data: readonly unknown[] = ITEMS_DATA;
  for (const r of data) {
    const raw = r as {
      kind?: unknown; name?: unknown; slot?: unknown; heroVariant?: unknown;
      icon?: unknown; shade?: unknown; stats?: unknown;
      weaponClass?: unknown; spellType?: unknown; spellColor?: unknown; spellRadius?: unknown;
    };
    if (!Number.isInteger(raw.kind) || (raw.kind as number) < 0) {
      throw new Error(`items.data: bad kind ${String(raw.kind)} (${String(raw.name)})`);
    }
    const kind = raw.kind as number;
    if (kinds.has(kind)) {
      throw new Error(`items.data: duplicate kind ${kind} (${String(raw.name)})`);
    }
    kinds.add(kind);
    if (typeof raw.slot !== "string" || !(raw.slot in HERO_SLOT)) {
      throw new Error(`items.data: ${String(raw.name)}: unknown slot ${String(raw.slot)}`);
    }
    if (typeof raw.name !== "string" || raw.name === "") {
      throw new Error(`items.data: kind ${kind}: missing name`);
    }
    if (typeof raw.heroVariant !== "string" || raw.heroVariant === "") {
      throw new Error(`items.data: ${raw.name}: missing heroVariant`);
    }
    if (typeof raw.icon !== "string" || raw.icon === "") {
      throw new Error(`items.data: ${raw.name}: missing icon`);
    }
    const stats: ItemStats = {};
    const rawStats = raw.stats as Record<string, unknown> | undefined;
    for (const [k, v] of Object.entries(rawStats ?? {})) {
      if (typeof v !== "number" || !Number.isSafeInteger(v) || (v < 0 && !isSignedEquipmentStat(k))) {
        throw new Error(`items.data: ${raw.name}: stat ${k} must be an integer (only attack/damage bonuses and defence may be negative)`);
      }
      stats[k] = v;
    }
    // Weapon class + spell (weapons only; the cross-field rules are the
    // store's, re-checked here so a hand-edited data module fails loudly).
    const slot = raw.slot as EquipSlot;
    let weaponClass: WeaponClass | undefined;
    let spellType: SpellType | undefined;
    let spellColor: string | undefined;
    let spellRadius: number | undefined;
    if (slot === "weapon") {
      if (raw.weaponClass === undefined) {
        weaponClass = "melee"; // the pre-class default
      } else if (typeof raw.weaponClass === "string" && (WEAPON_CLASSES as readonly string[]).includes(raw.weaponClass)) {
        weaponClass = raw.weaponClass as WeaponClass;
      } else {
        throw new Error(`items.data: ${raw.name}: bad weaponClass ${String(raw.weaponClass)}`);
      }
      const hasSpell = raw.spellType !== undefined || raw.spellColor !== undefined || raw.spellRadius !== undefined;
      if (weaponClass === "magic") {
        if (typeof raw.spellType !== "string" || !(SPELL_TYPES as readonly string[]).includes(raw.spellType)) {
          throw new Error(`items.data: ${raw.name}: magic weapon needs a spellType (${SPELL_TYPES.join(" | ")})`);
        }
        spellType = raw.spellType as SpellType;
        if (typeof raw.spellColor !== "string" || !/^#[0-9a-fA-F]{6}$/.test(raw.spellColor)) {
          throw new Error(`items.data: ${raw.name}: magic weapon needs a #rrggbb spellColor`);
        }
        spellColor = raw.spellColor.toLowerCase();
        if (typeof raw.spellRadius !== "number" || !Number.isInteger(raw.spellRadius) || raw.spellRadius < 1) {
          throw new Error(`items.data: ${raw.name}: magic weapon needs a spellRadius >= 1`);
        }
        spellRadius = raw.spellRadius;
      } else if (hasSpell) {
        throw new Error(`items.data: ${raw.name}: spell fields are only for magic weapons`);
      }
    } else if (raw.weaponClass !== undefined || raw.spellType !== undefined || raw.spellColor !== undefined || raw.spellRadius !== undefined) {
      throw new Error(`items.data: ${raw.name}: weaponClass/spell fields are weapon-only`);
    }
    out.push({
      kind,
      name: raw.name,
      slot,
      heroSlot: HERO_SLOT[slot],
      heroVariant: raw.heroVariant,
      icon: raw.icon,
      shade: typeof raw.shade === "string" ? raw.shade : null,
      stats,
      ...(weaponClass !== undefined ? { weaponClass } : {}),
      ...(spellType !== undefined ? { spellType, spellColor, spellRadius } : {}),
    });
  }
  return out;
}

/** All equippable item kinds, by kind index (may have gaps — permanent ids). */
export const ITEMS: readonly ItemDef[] = buildCatalog();

/** Slot display labels (HUD Equipment panel). */
export const SLOT_LABELS: Readonly<Record<EquipSlot, string>> = {
  weapon: "Weapon",
  shield: "Shield",
  helmet: "Helmet",
  chest: "Chest",
  legs: "Legs",
  feet: "Feet",
};

export function isEquipSlot(v: unknown): v is EquipSlot {
  return (EQUIP_SLOTS as readonly string[]).includes(v as string);
}

/** Catalog lookup; undefined for out-of-range/unknown kinds (the sim rejects
 *  those with an invalid event — decode stays loose, the sim stays strict). */
export function itemByKind(kind: number): ItemDef | undefined {
  return ITEMS.find((i) => i.kind === kind);
}
